import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class ThemesService {
  Theme = Theme;
  DialpadTheme = DialpadTheme;
  ThemeCssClass = ThemeCssClass;

  themes: Array<ThemeType> = [
    {
      tab: Theme.DEFAULT,
      cssClass: ThemeCssClass.DEFAULT,
      isActive: true,
      imageURL: '/assets/styles/assets/images/white-theme.png',
      enabled: true,
    },
    {
      tab: Theme.RED,
      cssClass: ThemeCssClass.RED,
      isActive: false,
      imageURL: '/assets/styles/assets/images/red-theme.png',
      enabled: true,
    },
    {
      tab: Theme.BLUE,
      cssClass: ThemeCssClass.BLUE,
      isActive: false,
      imageURL: '/assets/styles/assets/images/blue-theme.png',
      enabled: false,
    },
    {
      tab: Theme.GREEN,
      cssClass: ThemeCssClass.GREEN,
      isActive: false,
      imageURL: '/assets/styles/assets/images/green-theme.PNG',
      enabled: false,
    },
    // {
    //   tab: Theme.CUSTOM,
    //   cssClass: ThemeCssClass.CUSTOM,
    //   isActive: false,
    //   imageURL: '/assets/styles/assets/images/color-switcher.png',
    //   enabled: false,
    // },
    {
      tab: Theme.DARK,
      cssClass: ThemeCssClass.DARK,
      isActive: false,
      imageURL: '/assets/styles/assets/images/dark-theme-2.png',
      enabled: false,
    },
  ];
  selectedTheme = this.themes[Theme.DEFAULT];
  customColor = '#fa0060';

  dialpadThemes = [
    {
      tab: DialpadTheme.DEFAULT,
      isActive: true,
      imageURL: '/assets/images/dialpad-default.png',
      enabled: true,
    },
    {
      tab: DialpadTheme.NEUMORPHIC,
      isActive: false,
      imageURL: '/assets/images/dialpad-neumorphic.png',
      enabled: true,
    },
  ];
  selectedDialpadTheme = this.dialpadThemes[DialpadTheme.DEFAULT];

  constructor(public authService: AuthenticationService, private httpClient: HttpClient) {
    this.syncSelectedTheme();
    this.syncSelectedDialpadTheme();
  }

  /**
   * get widget label color
   * @returns color
   */
  getWidgetLabelColor(): string {
    return this.selectedTheme.tab === Theme.DARK ? '#ddd' : '#475467';
  }

  /**
   * get line chart colors
   * @returns colors array
   */
  getLineChartColors(): Array<string> {
    let colors;

    switch (this.selectedTheme.tab) {
      case Theme.DARK:
        colors = ['#12B76A', '#E55C32', '#f1b5b5', '#90e0ef', '#f3babc', '#f1f1f1'];
        break;
      default:
        colors = ['#344054', '#c9c9c9', '#667085', '#98A2B3', '#E4E7EC', '#27D4D4', '#17A2B8'];
        break;
    }
    return colors;
  }

  /**
   * get widget colors
   * @returns colors array
   */
  getWidgetColors(): Array<string> {
    let colors;

    switch (this.selectedTheme.tab) {
      case Theme.RED:
        colors = ['#FECDCA', '#FDA29B', '#F97066', '#F04438', '#D92D20', '#B42318', '#912018'];
        break;
      case Theme.BLUE:
        colors = ['#C8CCE5', '#9EA5D1', '#717BBC', '#4E5BA6', '#3E4784', '#363F72', '#293056'];
        break;
      case Theme.GREEN:
        colors = ['#6CE9A6', '#32D583', '#12B76A', '#039855', '#027A48', '#05603A', '#054F31'];
        break;
      case Theme.DARK:
        colors = ['#D1EA67', '#90C579', '#589E77', '#32766C', '#204D52', '#f1f1f1'];
        break;
      case Theme.CUSTOM:
        colors = ['#344054', '#E5E5F1', '#667085', '#98A2B3', '#bdbebf', '#a4a5a6', '#94989c'];
        break;
      default:
        colors = ['#344054', '#E5E5F1', '#667085', '#98A2B3', '#bdbebf', '#a4a5a6', '#94989c'];
        break;
    }
    return colors;
  }

  /**
   * get custom styles
   * @returns styles
   */
  getCustomStyles() {
    return {
      '--accent-bg-notif': this.customColor,
      '--accent-bg-toggle-link-opened': this.customColor,
      '--border-toggle-link-opened': this.customColor,
      '--accent-nav-active': this.customColor,
      '--accent-bg-tabs': this.customColor,
      '--accent-bg-btn-secondary': this.customColor,
      '--accent-card-icon': this.customColor,
      '--accent-bg-tabs-light': this.customColor,
      '--accent-bg-pills-icon': this.customColor,
      '--blue--darker': this.customColor,
      '--background-btn-with-border--shade': this.customColor,
      '--border-btn-with-border': this.customColor,
      '--accent-sidepanel-toggle-nav-hover': this.customColor,
    };
  }

  /**
   * get custom styles as string
   * @returns styles
   */
  getCustomStylesAsString() {
    return `
      --accent-bg-notif: ${this.customColor};
      --accent-bg-toggle-link-opened: ${this.customColor};
      --border-toggle-link-opened: ${this.customColor};
      --accent-nav-active: ${this.customColor};
      --accent-bg-tabs: ${this.customColor};
      --accent-bg-btn-secondary: ${this.customColor};
      --accent-card-icon: ${this.customColor};
      --accent-bg-tabs-light: ${this.customColor};
      --accent-bg-pills-icon: ${this.customColor};
      --blue--darker: ${this.customColor};
      --background-btn-with-border--shade: ${this.customColor};
      --border-btn-with-border: ${this.customColor};
      --accent-sidepanel-toggle-nav-hover: ${this.customColor};`;
  }

  /**
   * Change active theme
   * @param selectedTheme     Selected theme
   */
  onThemeChange(selectedTheme: Theme) {
    this.themes.forEach((theme) => {
      theme.isActive = theme.tab === selectedTheme;

      if (theme.isActive) this.selectedTheme = theme;
    });
  }

  /**
   * Change active dialpad theme
   * @param selectedTheme     Selected theme
   */
  onDialpadThemeChange(selectedTheme: DialpadTheme) {
    this.dialpadThemes.forEach((theme) => {
      theme.isActive = theme.tab === selectedTheme;

      if (theme.isActive) this.selectedDialpadTheme = theme;
    });
  }

  onCancelThemeUpdate() {
    this.onThemeChange(this.authService.getTheme());
  }

  /**
   * Change custom theme color
   * @param input     Color picker input
   */
  onCustomColorChange(input: EventTarget) {
    this.customColor = (input as HTMLInputElement).value;
  }

  /**
   * Update theme in user settings
   */
  updateTheme() {
    const url = `/user/userSettings`;

    this.httpClient
      .post(url, {
        Id: this.authService.authData.Id,
        Theme: this.selectedTheme.tab,
        DialpadTheme: this.selectedDialpadTheme.tab,
      })
      .subscribe({
        next: (res) => {
          this.authService.updateTheme(this.selectedTheme.tab, this.selectedDialpadTheme.tab);
        },
        error: ({ error }) => {
          console.error(error);
        },
      });
  }

  /**
   * sync selected theme
   */
  syncSelectedTheme() {
    const currentTheme = this.authService?.authData?.UserSettings?.Theme;
    this.onThemeChange(currentTheme || Theme.DEFAULT);
  }

  /**
   * sync selected dialpad theme
   */
  syncSelectedDialpadTheme() {
    const currentTheme = this.authService?.authData?.UserSettings?.DialpadTheme;
    this.onDialpadThemeChange(currentTheme || DialpadTheme.DEFAULT);
  }
}

export enum DialpadTheme {
  DEFAULT,
  NEUMORPHIC,
}

export enum Theme {
  DEFAULT,
  RED,
  BLUE,
  GREEN,
  CUSTOM,
  DARK,
}

export enum ThemeCssClass {
  DEFAULT = 'theme--default',
  RED = 'theme--red',
  BLUE = 'theme--blue',
  GREEN = 'theme--green',
  CUSTOM = 'theme--custom',
  DARK = 'theme--dark',
}

interface ThemeType {
  tab: Theme;
  cssClass: ThemeCssClass;
  isActive: boolean;
  imageURL: string;
  enabled: boolean;
}
